import { api, el, toast } from "../../ui/dom";
import { openModal } from "../../ui/modal";
import { area, refuse } from "../clients/forms";

// Diagnoses, on the patient and in the assessment (brief 1E-28; mockup v86 (6)): added from the
// ICD-10 list with a note (onset, status, who manages it), the note changed with the pencil, and
// each client's claims picking which diagnoses print in box 21 (Diagnoses › On claims).

type Answer = { ok: boolean; body: { error?: string } };

/** Add a diagnosis: search ICD-10, then a note, then Add. `done` reads the screen again. */
export function addDiagnosisModal(patientId: string, done: () => unknown) {
  const base = `/api/patients/${encodeURIComponent(patientId)}`;
  const q = el("input", {
    type: "search",
    id: "dx-q",
    placeholder: "Words or a code",
    autocomplete: "off",
  });
  const note = area("Note", "");
  note.input.placeholder = "Onset, status, who manages it";
  const results = el("div", { class: "picks", role: "list", "aria-label": "Diagnoses found" });
  const m = openModal({
    title: "Add a diagnosis",
    sub: "From the ICD-10 list (National Library of Medicine).",
    body: [
      el(
        "div",
        { class: "form", style: "max-width:none" },
        el("div", { class: "field" }, el("label", { for: "dx-q" }, "Search ICD-10"), q),
        note.el,
        results,
      ),
    ],
  });
  q.focus();
  let n = 0;
  q.addEventListener("input", async () => {
    const mine = ++n;
    const words = q.value.trim();
    if (words.length < 2) return results.replaceChildren();
    const res = await api<{ matches: { code: string; name: string }[] }>(
      "GET",
      `/api/icd10?q=${encodeURIComponent(words)}`,
    );
    if (mine !== n) return;
    if (!res.ok)
      return results.replaceChildren(
        el("p", { class: "small muted" }, res.body.error ?? "The lookup didn't answer."),
      );
    results.replaceChildren(
      ...(res.body.matches.length
        ? res.body.matches.map((d) => {
            const b = el(
              "button",
              { type: "button", class: "btn", role: "listitem" },
              el("b", {}, d.code),
              ` ${d.name}`,
            );
            b.addEventListener("click", async () => {
              b.disabled = true;
              const res2 = (await api("POST", `${base}/diagnoses`, {
                code: d.code,
                description: d.name,
                note: note.input.value,
              })) as Answer;
              b.disabled = false;
              if (!res2.ok) return refuse(m.note, res2.body.error);
              m.close();
              toast("Diagnosis added", `${d.code} ${d.name}`);
              await done();
            });
            return b;
          })
        : [el("p", { class: "small muted" }, "Nothing matches.")]),
    );
  });
}

/** The pencil on a diagnosis: its note. */
export function diagnosisNoteModal(
  patientId: string,
  d: { id: string; code: string; description: string; note: string | null },
  done: () => unknown,
) {
  const note = area("Note", d.note ?? "");
  note.input.placeholder = "Onset, status, who manages it";
  const save = el("button", { type: "button", class: "btn pri" }, "Save");
  const m = openModal({
    title: `${d.code} ${d.description}`,
    body: [el("div", { class: "form" }, note.el)],
    actions: [save],
  });
  save.addEventListener("click", async () => {
    save.disabled = true;
    const res = (await api(
      "PUT",
      `/api/patients/${encodeURIComponent(patientId)}/diagnoses/${d.id}`,
      { note: note.input.value },
    )) as Answer;
    save.disabled = false;
    if (!res.ok) return refuse(m.note, res.body.error);
    m.close();
    toast("Note saved");
    await done();
  });
  note.input.focus();
}

export interface ClaimPick {
  clientId: string;
  client: string;
  picked: boolean;
  diagnosisIds: string[];
  codes: string[];
}

/** "On claims: all" or the codes picked, for the client the patient's episode bills. */
export const onClaimsWords = (p: ClaimPick | undefined) =>
  `On claims: ${!p || !p.picked ? "all" : p.codes.join(", ") || "none"}`;

/** Diagnoses › On claims: which diagnoses print in box 21 for one client. */
export function claimDiagnosesModal(
  patientId: string,
  picks: readonly ClaimPick[],
  diagnoses: readonly { id: string; code: string; description: string }[],
  clientId: string | null,
  done: () => unknown,
) {
  let pick = picks.find((p) => p.clientId === clientId) ?? picks[0];
  if (!pick) return toast("No client bills this patient yet");
  const clientSel = el(
    "select",
    { id: "dxc-client" },
    ...picks.map((p) => el("option", { value: p.clientId, selected: p === pick }, p.client)),
  );
  const box = el("div", { class: "tsf", role: "group", "aria-label": "Diagnoses" });
  const drawBoxes = () =>
    box.replaceChildren(
      ...diagnoses.map((d) => {
        const c = el("input", {
          type: "checkbox",
          value: d.id,
          checked: (pick as ClaimPick).diagnosisIds.includes(d.id),
        });
        return el("label", { class: "tsr" }, c, " ", el("b", {}, d.code), ` ${d.description}`);
      }),
    );
  drawBoxes();
  clientSel.addEventListener("change", () => {
    pick = picks.find((p) => p.clientId === clientSel.value) ?? pick;
    m.root.querySelector(".muted.small")?.replaceChildren(`Box 21 for ${pick?.client ?? ""}`);
    drawBoxes();
  });
  const save = el("button", { type: "button", class: "btn pri" }, "Save");
  const m = openModal({
    title: "Diagnoses on claims",
    sub: `Box 21 for ${pick.client}`,
    body: [
      el(
        "div",
        { class: "form" },
        picks.length > 1
          ? el("div", { class: "field" }, el("label", { for: "dxc-client" }, "Client"), clientSel)
          : null,
        box,
      ),
    ],
    actions: [save],
  });
  save.addEventListener("click", async () => {
    const ids = [...box.querySelectorAll<HTMLInputElement>("input:checked")].map((i) => i.value);
    save.disabled = true;
    const res = (await api(
      "PUT",
      `/api/patients/${encodeURIComponent(patientId)}/claim-diagnoses`,
      { clientId: pick?.clientId, diagnosisIds: ids },
    )) as Answer;
    save.disabled = false;
    if (!res.ok) return refuse(m.note, res.body.error);
    m.close();
    toast("Saved", `Box 21 for ${pick?.client ?? "this client"}`);
    await done();
  });
}
